/**
 * Structured listing attributes stored on each stay (`listingAttributes`).
 * Search secondary filters use the same keys — filters match stored values.
 */

export const mergeListingAttributes = (loaded) => {
  const merged = {
    ...createEmptyListingAttributes(),
    ...(loaded && typeof loaded === 'object' ? loaded : {})
  };
  if (merged.powerBackup === 'Inverter' || merged.powerBackup === 'Generator') {
    merged.powerBackup = 'Yes';
  }
  return merged;
};

export function createEmptyListingAttributes() {
  return {
    // Essentials & comfort
    airConditioning: null,
    heating: null,
    wifi: null,
    powerBackup: null,
    hotWater: null,
    waterSupply24x7: null,
    housekeeping: null,
    laundry: null,
    // Meals & kitchen
    mealsAvailable: [],
    dogMealsAvailable: null,
    kitchenAccess: null,
    stoveInductionMicrowave: null,
    refrigerator: null,
    cookChef: null,
    bbqBonfire: null,
    roWater: null,
    // Accessibility
    wheelchairAccessible: null,
    wideDoorways: null,
    stepFreeBedroom: null,
    accessibleBathroom: null,
    groundFloorOrLift: null,
    parkingCloseToEntrance: null,
    // Outdoors & pet safety
    fencedProperty: null,
    privateGarden: null,
    balconyTerrace: null,
    nearbyParkDistance: null,
    outdoorTap: null,
    petAmenities: [],
    // Family & group
    bedroomsCount: null,
    bathroomsCount: null,
    extraMattresses: null,
    cribHighChair: null,
    indoorGamesTvOtt: null,
    privatePool: null,
    // Work & connectivity
    dedicatedWorkspace: null,
    powerOutletsAtDesk: null,
    wifiSpeedVerified: null,
    // Parking & transport
    parkingOnsite: null,
    evCharger: null,
    roadAccess: null,
    distanceFromCityKm: null,
    // Safety & house rules
    cctvCommonAreas: null,
    caretakerSecurity: null,
    firstAidKit: null,
    fireExtinguisher: null,
    quietHours: null,
    smokingAllowed: null,
    alcoholAllowed: null,
    visitorsAllowed: null
  };
}

/** UI sections for admin/vendor editor (and optional filter UI) */
export const LISTING_ATTRIBUTE_SECTIONS = [
  {
    id: 'essentials',
    title: 'Essentials & comfort',
    fields: [
      { key: 'airConditioning', label: 'Air conditioning', type: 'yesno' },
      { key: 'heating', label: 'Heating / room heater', type: 'yesno' },
      { key: 'wifi', label: 'Wi‑Fi', type: 'yesno' },
      { key: 'powerBackup', label: 'Power backup', type: 'yesno' },
      { key: 'hotWater', label: 'Hot water / geyser', type: 'yesno' },
      { key: 'waterSupply24x7', label: '24×7 water supply', type: 'yesno' },
      {
        key: 'housekeeping',
        label: 'Housekeeping',
        type: 'select',
        options: ['Daily', 'On-request']
      },
      {
        key: 'laundry',
        label: 'Laundry / washing machine',
        type: 'select',
        options: ['On-site', 'Paid']
      }
    ]
  },
  {
    id: 'meals',
    title: 'Meals & kitchen',
    fields: [
      {
        key: 'mealsAvailable',
        label: 'Meals available',
        type: 'multiselect',
        options: ['Veg', 'Non-Veg', 'Jain']
      },
      { key: 'dogMealsAvailable', label: 'Dog meals (on request)', type: 'yesno' },
      {
        key: 'kitchenAccess',
        label: 'Kitchen access',
        type: 'select',
        options: ['Private', 'Shared']
      },
      {
        key: 'stoveInductionMicrowave',
        label: 'Stove / induction / microwave',
        type: 'select',
        options: ['Stove', 'Induction', 'Microwave', 'Multiple']
      },
      { key: 'refrigerator', label: 'Refrigerator', type: 'yesno' },
      {
        key: 'cookChef',
        label: 'Cook / chef',
        type: 'select',
        options: ['On-call', 'Included']
      },
      {
        key: 'bbqBonfire',
        label: 'BBQ / bonfire setup',
        type: 'select',
        options: ['On request', 'Paid']
      },
      { key: 'roWater', label: 'RO / filtered drinking water', type: 'yesno' }
    ]
  },
  {
    id: 'accessibility',
    title: 'Accessibility',
    fields: [
      { key: 'wheelchairAccessible', label: 'Wheelchair accessible (step‑free entrance)', type: 'yesno' },
      { key: 'wideDoorways', label: 'Wide doorways / corridors', type: 'yesno' },
      { key: 'stepFreeBedroom', label: 'Step‑free bedroom', type: 'yesno' },
      { key: 'accessibleBathroom', label: 'Accessible bathroom (grab bars / shower chair)', type: 'yesno' },
      { key: 'groundFloorOrLift', label: 'Ground floor / lift', type: 'yesno' },
      { key: 'parkingCloseToEntrance', label: 'Parking close to entrance (≤ 50 m)', type: 'yesno' }
    ]
  },
  {
    id: 'outdoors',
    title: 'Outdoors & pet safety',
    fields: [
      { key: 'fencedProperty', label: 'Fenced property / enclosed lawn', type: 'yesno' },
      {
        key: 'privateGarden',
        label: 'Private garden',
        type: 'select',
        options: ['Small', 'Medium', 'Large']
      },
      { key: 'balconyTerrace', label: 'Balcony / terrace (pet‑safe)', type: 'yesno' },
      {
        key: 'nearbyParkDistance',
        label: 'Nearby park / trail',
        type: 'select',
        options: ['≤500 m', '≤1 km', '≤2 km']
      },
      { key: 'outdoorTap', label: 'Outdoor tap / pet wash area', type: 'yesno' },
      {
        key: 'petAmenities',
        label: 'Pet amenities',
        type: 'multiselect',
        options: ['Bowls', 'Beds', 'Towels', 'Waste bags', 'First aid']
      }
    ]
  },
  {
    id: 'family',
    title: 'Family & group comfort',
    fields: [
      { key: 'bedroomsCount', label: 'Bedrooms (count)', type: 'number', min: 0, max: 20 },
      { key: 'bathroomsCount', label: 'Bathrooms (count)', type: 'number', min: 0, max: 20 },
      { key: 'extraMattresses', label: 'Extra mattresses / sofa‑bed', type: 'yesno' },
      { key: 'cribHighChair', label: 'Crib / high chair (on request)', type: 'yesno' },
      { key: 'indoorGamesTvOtt', label: 'Indoor games / TV / OTT', type: 'yesno' },
      { key: 'privatePool', label: 'Private pool', type: 'yesno' }
    ]
  },
  {
    id: 'work',
    title: 'Work & connectivity',
    fields: [
      { key: 'dedicatedWorkspace', label: 'Dedicated workspace / desk & chair', type: 'yesno' },
      { key: 'powerOutletsAtDesk', label: 'Multiple power outlets near desk', type: 'yesno' },
      { key: 'wifiSpeedVerified', label: 'Wi‑Fi speed verified', type: 'yesno' }
    ]
  },
  {
    id: 'parking',
    title: 'Parking & transport',
    fields: [
      {
        key: 'parkingOnsite',
        label: 'Parking on‑site',
        type: 'select',
        options: ['Open', 'Covered']
      },
      {
        key: 'evCharger',
        label: 'EV charger',
        type: 'select',
        options: ['On-site', 'Nearby']
      },
      {
        key: 'roadAccess',
        label: 'Road access',
        type: 'select',
        options: ['SUV friendly', 'Last-mile rough road']
      },
      { key: 'distanceFromCityKm', label: 'Distance from city (km)', type: 'number', min: 0, max: 500 }
    ]
  },
  {
    id: 'safety',
    title: 'Safety & house rules',
    fields: [
      { key: 'cctvCommonAreas', label: 'CCTV in common areas', type: 'yesno' },
      { key: 'caretakerSecurity', label: 'Caretaker / security on‑site', type: 'yesno' },
      { key: 'firstAidKit', label: 'First‑aid / pet first‑aid', type: 'yesno' },
      { key: 'fireExtinguisher', label: 'Fire extinguisher', type: 'yesno' },
      { key: 'quietHours', label: 'Quiet hours / no loud music after 10 pm', type: 'yesno' },
      { key: 'smokingAllowed', label: 'Smoking allowed', type: 'yesno' },
      { key: 'alcoholAllowed', label: 'Alcohol allowed', type: 'yesno' },
      { key: 'visitorsAllowed', label: 'Visitors allowed', type: 'yesno' }
    ]
  }
];
